/** Where the manifest and the markdown files are served from (see `public/help-docs`). */
export const HELP_CONTENT_BASE = '/help-docs';

/**
 * One entry in the help tree: a section, a topic, or both.
 *
 * What kind of node it is comes from its shape rather than from a `type` field: a node carrying a
 * `file` is a topic the tree can navigate to, and a node carrying `children` is a section that
 * expands.
 */
export interface HelpNode {
  /**
   * The URL slug and the only route parameter the reader takes (`/help/:topicId`).
   * Unique across the whole tree, not just within its section.
   */
  id: string;

  /** The title shown in the tree and above the article. */
  title: string;

  /** A bootstrap-icons name without the `bi-` prefix. */
  icon?: string;

  /** The markdown file's name, relative to the language folder. Present on topics. */
  file?: string;

  /** Roughly how long the page takes to read, shown beside its title. */
  readingMinutes?: number;

  /** An image drawn edge to edge above the title, as a URL under `/help-docs/img`. */
  hero?: string;

  /** Alternative text for the hero image. */
  heroAlt?: string;

  /** Whether the section starts open. Only meaningful for a node with children. */
  defaultExpanded?: boolean;

  children?: HelpNode[];
}

/** The whole tree, as `help-docs/manifest.json` holds it. */
export interface HelpManifest {
  version: number;

  /** The language folder the markdown files live in. */
  defaultLang: string;

  nodes: HelpNode[];
}

/** Every navigable topic in the tree, depth-first, in the order it is drawn. */
export function flattenTopics(nodes: readonly HelpNode[]): HelpNode[] {
  return nodes.flatMap((node) => [...(node.file ? [node] : []), ...flattenTopics(node.children ?? [])]);
}

/** The topic with this id, or null when the tree has no such page. */
export function findTopic(nodes: readonly HelpNode[], id: string): HelpNode | null {
  return flattenTopics(nodes).find((topic) => topic.id === id) ?? null;
}

/** Any node (section or topic) with this id, or null. */
export function findNode(nodes: readonly HelpNode[], id: string): HelpNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }

    const inChildren = findNode(node.children ?? [], id);

    if (inChildren) {
      return inChildren;
    }
  }

  return null;
}

/**
 * The ids of the sections containing this topic, outermost first.
 * Used to open the tree on a deep link. Empty when the id is unknown or sits at the root.
 */
export function ancestorIds(nodes: readonly HelpNode[], id: string): string[] {
  for (const node of nodes) {
    if (node.id === id) {
      return [];
    }

    const children = node.children ?? [];

    if (containsTopic(children, id)) {
      return [node.id, ...ancestorIds(children, id)];
    }
  }

  return [];
}

function containsTopic(nodes: readonly HelpNode[], id: string): boolean {
  return nodes.some((node) => node.id === id || containsTopic(node.children ?? [], id));
}
